import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle } from 'lucide-react';
import { type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router';

interface CouponRedeemProtectedRouteProps {
  children: ReactNode;
}

export const CouponRedeemProtectedRoute: React.FC<CouponRedeemProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Check if the user came from /meine-gutscheine
  const isFromMyVouchers =
    document.referrer.includes('/meine-gutscheine') || location.state?.from === '/meine-gutscheine';

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto'></div>
          <p className='mt-2 text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // Check if user has proper access (came from my vouchers page or has voucher in state)
  const hasVoucherId = location.state?.voucherId;
  const hasProperAccess = isFromMyVouchers || hasVoucherId;

  if (!hasProperAccess) {
    // Show access denied message with instruction
    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <div className='max-w-md w-full space-y-4'>
          <Alert>
            <AlertTriangle className='h-4 w-4' />
            <AlertDescription>
              Diese Seite ist nur über "Meine Gutscheine" zugänglich. Bitte wählen Sie dort einen Gutschein zum Einlösen
              aus.
            </AlertDescription>
          </Alert>
          <div className='text-center'>
            <Button asChild className='bg-cyan-600 hover:bg-cyan-700'>
              <Link to='/meine-gutscheine'>Zu meinen Gutscheinen</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
